"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { PlatformError } from "@/lib/api/client";
import { useMagicLink, useSignInMethods, useWalletSignIn } from "@/lib/api/session";
import { hasSocialMethod, offeredMethods } from "@/lib/api/signin-methods";
import { accountUnavailableFrom, describeAccountUnavailable } from "@/lib/api/signin-outcome";
import { useDirectPlatformBase, useTelegramWidgetAllowed } from "@/lib/api/social";
import { WalletError } from "@/lib/wallet/types";
import { useWallets } from "@/lib/wallet/use-wallets";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { Button } from "@/components/ui/primitives";
import { SocialSignIn } from "./SocialSignIn";

/* Sign-in against the real endpoints.

   Which methods appear is decided by `GET /signin`, not by us — a site with
   Telegram off does not get a Telegram button (lib/api/signin-methods.ts).
   Failures are shown as the platform reports them: a 403 with no reason means
   our origin is not on the site's allowlist, which is a deployment fact the
   visitor cannot fix, so it is labelled as such rather than dressed up as a
   bad email; a 403 naming a closed or self-excluded account says that. */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* The age and lawfulness assertion is a compliance fact, not marketing copy, so
   it is a constant here rather than an admin-editable field — the same move the
   BetBy renderer URL got in `src/components/sports/Sportsbook.tsx:30`. It spent
   one sweep as `branding.signInDisclosure`, an unvalidated textarea, and the
   browser pass found that saving it empty removed the 18+ statement from this
   dialog entirely while the screen still looked correct. */
const AGE_DISCLOSURE =
  "By continuing you confirm that you are at least 18 years old and that online gambling is lawful where you live.";

export function SignInModal({
  open,
  onClose,
  initialEmail = "",
}: {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
}) {
  const methods = useSignInMethods();
  const magicLink = useMagicLink();
  const walletSignIn = useWalletSignIn();

  const [email, setEmail] = useState(initialEmail);
  const [touched, setTouched] = useState(false);

  /* Opened from a form that already took the address: carry it in. */
  useEffect(() => {
    if (open && initialEmail) setEmail(initialEmail);
  }, [open, initialEmail]);
  const [walletsOpen, setWalletsOpen] = useState(false);
  const wallets = useWallets(open && walletsOpen);

  const panel = useModalChrome(open, onClose);

  const emailValid = EMAIL.test(email.trim());
  const available = methods.data;
  /* Google and X need the browser to reach the API directly (signin-methods.ts). */
  const directBase = useDirectPlatformBase();
  const telegramWidget = useTelegramWidgetAllowed();
  const ways = offeredMethods(available, { oauth: directBase !== null, telegramWidget });
  const offered =
    wallets?.filter((wallet) => (wallet.chain === "evm" ? available?.siwe : available?.siws)) ?? null;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Sign in"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="modal-shadow relative flex w-full max-w-[400px] max-h-[calc(100dvh-2rem)] flex-col items-center gap-[12px] overflow-y-auto rounded-2xl border border-border-input bg-elevation-1 p-[24px]"
          >
            <header className="flex w-full items-center gap-[4px]">
              <h2 className="flex-1 text-[15.75px] font-bold leading-[1.6] tracking-normal lg:text-[17.5px]">Sign in</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid h-[24px] w-[24px] shrink-0 place-items-center text-fg transition-colors hover:text-fg-muted"
              >
                <Icon name="close" size={18} />
              </button>
            </header>

            <div className="flex w-full flex-col gap-[12px]">
              {methods.isPending && <p className="text-[12px] leading-[1.6] text-fg-subtle">Checking what this site supports…</p>}

              {methods.isError && (
                <Notice tone="error">
                  Could not load the sign-in options. The platform did not answer.
                </Notice>
              )}

              {available?.magicLink && (
                <form
                  className="flex flex-col gap-[4px]"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setTouched(true);

                    if (emailValid) {
                      magicLink.mutate(email.trim());
                    }
                  }}
                >
                  <label htmlFor="signin-email" className="text-[12px] font-bold leading-[1.6] text-fg-subtle">
                    Email
                  </label>
                  <input
                    id="signin-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    aria-invalid={touched && !emailValid}
                    className={cn(
                      "h-[42px] rounded-lg border bg-elevation-2 px-[12px] py-[8px] text-[12px] font-bold leading-[1.6] text-fg outline-none transition-colors placeholder:text-fg-subtle",
                      touched && !emailValid ? "border-error" : "border-border-input focus:border-brand",
                    )}
                  />
                  {touched && !emailValid && (
                    <p className="text-[9px] leading-[1.5] text-error-fg">Enter an email address to continue.</p>
                  )}

                  <Button type="submit" size="lg" className="mt-[8px] w-full" disabled={magicLink.isPending}>
                    {magicLink.isPending ? "Sending…" : "Email me a link"}
                  </Button>
                </form>
              )}

              {magicLink.isSuccess && (
                <Notice tone="ok">
                  Link sent. It expires at{" "}
                  {new Date(magicLink.data.expiresAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  .
                </Notice>
              )}

              {magicLink.isError && <SignInFailure error={magicLink.error} />}

              {hasSocialMethod(ways) && (
                <>
                  {ways.magicLink && <Divider />}
                  <SocialSignIn offered={ways} active={open} onSignedIn={onClose} />
                </>
              )}

              {(available?.siwe || available?.siws) && (
                <>
                  <Divider />

                  {!walletsOpen && (
                    <button
                      type="button"
                      onClick={() => setWalletsOpen(true)}
                      className="flex h-[46px] items-center justify-center gap-[8px] rounded-lg bg-elevation-2 text-[14px] font-bold leading-[1.6] capitalize tracking-[0.28px] text-fg transition-colors hover:bg-elevation-3"
                    >
                      <Icon name="link" size={16} />
                      Continue with a wallet
                    </button>
                  )}

                  {walletsOpen && offered === null && (
                    <p className="text-[12px] leading-[1.6] text-fg-subtle">Looking for wallets…</p>
                  )}

                  {walletsOpen && offered?.length === 0 && (
                    <Notice tone="info">
                      No wallet was found in this browser. Install MetaMask or Phantom, then open this
                      dialog again.
                    </Notice>
                  )}

                  {walletsOpen && offered && offered.length > 0 && (
                    <div className="flex flex-col gap-[8px]">
                      {offered.map((wallet) => {
                        const busy = walletSignIn.isPending && walletSignIn.variables.id === wallet.id;

                        return (
                          <button
                            key={wallet.id}
                            type="button"
                            disabled={walletSignIn.isPending}
                            onClick={() => walletSignIn.mutate(wallet, { onSuccess: onClose })}
                            className="flex h-[46px] items-center gap-[8px] rounded-lg bg-elevation-2 px-[12px] text-[14px] font-bold leading-[1.6] tracking-[0.28px] text-fg transition-colors hover:bg-elevation-3 disabled:opacity-40"
                          >
                            {wallet.icon ? (
                              <img src={wallet.icon} alt="" className="h-[18px] w-[18px] rounded-[4px]" />
                            ) : (
                              <Icon name="link" size={16} />
                            )}
                            <span className="flex-1 text-left">{wallet.name}</span>
                            {busy && (
                              <span className="text-[12px] font-normal text-fg-subtle">
                                Check your wallet…
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {walletSignIn.isError && <WalletFailure error={walletSignIn.error} />}
                </>
              )}

              <p className="text-[9px] leading-[1.5] text-fg-subtle">
                {AGE_DISCLOSURE}{" "}
                <Link
                  href="/legal/terms-of-service"
                  onClick={onClose}
                  className="font-bold text-brand-bold underline"
                >
                  Read the terms
                </Link>
                .
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* A 403 from sign-in is not a user error — but it is two different platform
   refusals wearing one status code, and they have different owners. A geoblock
   carries `details.reason: "SITE_GEOBLOCKED"` and is about where the visitor
   is; the origin gate answers an empty 403 and is about where this app is
   served from (docs/platform-changes.md §1). Conflating them sent people to
   fix an allowlist that was never the problem — and vice versa. */
const isGeoblocked = (details: unknown) =>
  typeof details === "object" &&
  details !== null &&
  (details as { reason?: unknown }).reason === "SITE_GEOBLOCKED";

function SignInFailure({ error }: { error: unknown }) {
  const account = accountUnavailableFrom(error);

  if (account) {
    return <Notice tone="error">{describeAccountUnavailable(account)}</Notice>;
  }

  if (error instanceof PlatformError && error.kind === "forbidden") {
    if (isGeoblocked(error.details)) {
      return (
        <Notice tone="error">
          Sign-in isn&rsquo;t available from your region. This comes from the platform&rsquo;s own
          geoblocking, not from this site.
        </Notice>
      );
    }

    return (
      <Notice tone="error">
        This site does not accept sign-in from <code className="font-bold">{typeof window === "undefined" ? "" : window.location.origin}</code>.
        The origin has to be added to the site&rsquo;s allowlist on the platform first — nothing you can
        do from here.
      </Notice>
    );
  }

  if (error instanceof PlatformError && error.kind === "invalid") {
    return <Notice tone="error">That email was rejected. Check it and try again.</Notice>;
  }

  if (error instanceof PlatformError && error.kind === "rate-limited") {
    return <Notice tone="error">Too many attempts. Wait a moment before trying again.</Notice>;
  }

  return <Notice tone="error">Could not send the link. The platform did not answer.</Notice>;
}

function WalletFailure({ error }: { error: unknown }) {
  if (error instanceof WalletError) {
    if (error.kind === "rejected") {
      return <Notice tone="error">You rejected the request in your wallet.</Notice>;
    }

    return <Notice tone="error">{error.message}</Notice>;
  }

  const account = accountUnavailableFrom(error);

  if (account) {
    return <Notice tone="error">{describeAccountUnavailable(account)}</Notice>;
  }

  if (error instanceof PlatformError && error.kind === "forbidden" && isGeoblocked(error.details)) {
    return (
      <Notice tone="error">
        Sign-in isn&rsquo;t available from your region. This comes from the platform&rsquo;s own
        geoblocking, not from this site.
      </Notice>
    );
  }

  if (error instanceof PlatformError && error.kind === "forbidden") {
    return (
      <Notice tone="error">
        This site does not accept sign-in from{" "}
        <code className="font-bold">{typeof window === "undefined" ? "" : window.location.origin}</code>.
        The origin has to be added to the site&rsquo;s allowlist on the platform first.
      </Notice>
    );
  }

  if (error instanceof PlatformError && (error.kind === "unauthorized" || error.kind === "invalid")) {
    return <Notice tone="error">The platform rejected that signature. Try signing in again.</Notice>;
  }

  return <Notice tone="error">Could not finish wallet sign-in. The platform did not answer.</Notice>;
}

function Divider() {
  return (
    <span className="flex items-center gap-[12px] text-[9px] font-bold uppercase leading-[1.5] text-fg-disabled">
      <span className="h-px flex-1 bg-border" />
      or
      <span className="h-px flex-1 bg-border" />
    </span>
  );
}

function Notice({ tone, children }: { tone: "ok" | "error" | "info"; children: React.ReactNode }) {
  return (
    <p
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        "rounded-lg px-[12px] py-[8px] text-[12px] leading-[1.6]",
        tone === "ok" && "bg-elevation-2 text-success-fg",
        tone === "error" && "bg-elevation-2 text-error-fg",
        tone === "info" && "bg-elevation-2 text-fg-subtle",
      )}
    >
      {children}
    </p>
  );
}
